import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  LeadSchedulerSettings,
  LeadSchedulerSettingsMap,
  SchedulerTemplate,
  SchedulerTemplatePayload,
  schedulerService,
} from '@services/schedulerService';
import { CACHE_DURATION } from '../utils/cache';

interface SchedulerState {
  templates: SchedulerTemplate[];
  settingsByProjectId: LeadSchedulerSettingsMap;
  isLoadingTemplates: boolean;
  isLoadingSettings: boolean;
  isSaving: boolean;
  error: string | null;
  templatesLastFetched: number | null;
  settingsLastFetched: number | null;
}

const initialState: SchedulerState = {
  templates: [],
  settingsByProjectId: {},
  isLoadingTemplates: false,
  isLoadingSettings: false,
  isSaving: false,
  error: null,
  templatesLastFetched: null,
  settingsLastFetched: null,
};

export const fetchSchedulerTemplates = createAsyncThunk<
  SchedulerTemplate[],
  { force?: boolean } | undefined,
  { state: { scheduler: SchedulerState } }
>('scheduler/fetchTemplates', async (options, { getState, rejectWithValue }) => {
  const force = options?.force ?? false;
  const state = getState().scheduler;

  if (
    !force &&
    state.templates.length > 0 &&
    state.templatesLastFetched &&
    Date.now() - state.templatesLastFetched < CACHE_DURATION
  ) {
    return state.templates;
  }

  try {
    return await schedulerService.getTemplates();
  } catch (err) {
    return rejectWithValue((err as Error).message || 'Failed to load scheduler templates');
  }
});

export const createSchedulerTemplate = createAsyncThunk<
  SchedulerTemplate,
  SchedulerTemplatePayload
>('scheduler/createTemplate', async (template, { rejectWithValue }) => {
  try {
    return await schedulerService.createTemplate(template);
  } catch (err) {
    return rejectWithValue((err as Error).message || 'Failed to create scheduler template');
  }
});

export const updateSchedulerTemplate = createAsyncThunk<
  SchedulerTemplate,
  { id: string; updates: Partial<SchedulerTemplatePayload> }
>('scheduler/updateTemplate', async ({ id, updates }, { rejectWithValue }) => {
  try {
    return await schedulerService.updateTemplate(id, updates);
  } catch (err) {
    return rejectWithValue((err as Error).message || 'Failed to update scheduler template');
  }
});

export const deleteSchedulerTemplate = createAsyncThunk<string, string>(
  'scheduler/deleteTemplate',
  async (id, { rejectWithValue }) => {
    try {
      await schedulerService.deleteTemplate(id);
      return id;
    } catch (err) {
      return rejectWithValue((err as Error).message || 'Failed to delete scheduler template');
    }
  }
);

export const fetchLeadSchedulerSettings = createAsyncThunk<
  LeadSchedulerSettingsMap,
  { force?: boolean } | undefined,
  { state: { scheduler: SchedulerState } }
>('scheduler/fetchSettings', async (options, { getState, rejectWithValue }) => {
  const force = options?.force ?? false;
  const state = getState().scheduler;

  if (
    !force &&
    state.settingsLastFetched &&
    Date.now() - state.settingsLastFetched < CACHE_DURATION
  ) {
    return state.settingsByProjectId;
  }

  try {
    return await schedulerService.getSettings();
  } catch (err) {
    return rejectWithValue((err as Error).message || 'Failed to load scheduler settings');
  }
});

export const saveLeadSchedulerSettings = createAsyncThunk<
  { projectId: string; settings: LeadSchedulerSettings },
  { projectId: string; settings: LeadSchedulerSettings }
>('scheduler/saveProjectSettings', async ({ projectId, settings }, { rejectWithValue }) => {
  try {
    const saved = await schedulerService.saveProjectSettings(projectId, settings);
    return { projectId, settings: saved };
  } catch (err) {
    return rejectWithValue((err as Error).message || 'Failed to save scheduler settings');
  }
});

const schedulerSlice = createSlice({
  name: 'scheduler',
  initialState,
  reducers: {
    clearSchedulerError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchSchedulerTemplates.pending, state => {
        state.isLoadingTemplates = true;
        state.error = null;
      })
      .addCase(fetchSchedulerTemplates.fulfilled, (state, action) => {
        state.isLoadingTemplates = false;
        state.templates = action.payload;
        state.templatesLastFetched = Date.now();
      })
      .addCase(fetchSchedulerTemplates.rejected, (state, action) => {
        state.isLoadingTemplates = false;
        state.error = action.payload as string;
      })
      .addCase(createSchedulerTemplate.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(createSchedulerTemplate.fulfilled, (state, action) => {
        state.isSaving = false;
        state.templates.push(action.payload);
        state.templatesLastFetched = Date.now();
      })
      .addCase(createSchedulerTemplate.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      })
      .addCase(updateSchedulerTemplate.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateSchedulerTemplate.fulfilled, (state, action) => {
        state.isSaving = false;
        const index = state.templates.findIndex(template => template.id === action.payload.id);
        if (index !== -1) {
          state.templates[index] = action.payload;
        }
        state.templatesLastFetched = Date.now();
      })
      .addCase(updateSchedulerTemplate.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      })
      .addCase(deleteSchedulerTemplate.fulfilled, (state, action) => {
        state.templates = state.templates.filter(template => template.id !== action.payload);
        state.templatesLastFetched = Date.now();
      })
      .addCase(fetchLeadSchedulerSettings.pending, state => {
        state.isLoadingSettings = true;
        state.error = null;
      })
      .addCase(fetchLeadSchedulerSettings.fulfilled, (state, action) => {
        state.isLoadingSettings = false;
        state.settingsByProjectId = action.payload;
        state.settingsLastFetched = Date.now();
      })
      .addCase(fetchLeadSchedulerSettings.rejected, (state, action) => {
        state.isLoadingSettings = false;
        state.error = action.payload as string;
      })
      .addCase(saveLeadSchedulerSettings.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(saveLeadSchedulerSettings.fulfilled, (state, action) => {
        state.isSaving = false;
        state.settingsByProjectId[action.payload.projectId] = action.payload.settings;
        state.settingsLastFetched = Date.now();
      })
      .addCase(saveLeadSchedulerSettings.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearSchedulerError } = schedulerSlice.actions;
export default schedulerSlice.reducer;
